import { ANDROID_SIGNING_SHA256 } from './androidSigning';
export const AUTH_MESSAGES = {
  // Login messages
  ENTER_EMAIL_PASSWORD: 'Please enter both email and password',
  EMAIL_VERIFICATION_REQUIRED: 'Email verification required. Please check your email or click resend verification.',
  INCORRECT_CREDENTIALS: 'Incorrect email or password. Please try again.',
  NO_ACCOUNT_FOUND: 'No account found with this email address.',
  LOGIN_FAILED_CREDENTIALS: 'Login failed. Please check your credentials.',
  LOGIN_FAILED_TRY_AGAIN: 'Login failed. Please try again.',
  UNEXPECTED_ERROR: 'An unexpected error occurred. Please try again.',
  
  // Registration messages
  PASSWORDS_DO_NOT_MATCH: 'Passwords do not match',
  PASSWORD_MIN_LENGTH: 'Password must be at least 6 characters',
  REGISTRATION_SUCCESS: 'Registration successful! Please check your email and verify your account before logging in.',
  EMAIL_ALREADY_REGISTERED: 'Email address is already registered. Please use a different email or try logging in.',
  PASSWORD_TOO_WEAK: 'Password is too weak. Please choose a stronger password.',
  INVALID_EMAIL_ADDRESS: 'Please enter a valid email address.',
  REGISTRATION_FAILED: 'Registration failed. Please try again.',
  
  // Password reset messages
  ENTER_EMAIL_FOR_RESET: 'Please enter your email address to reset password.',
  PASSWORD_RESET_UNAVAILABLE: 'Password reset is currently not available. Please contact support.',
  ENTER_EMAIL_ADDRESS_FIRST: 'Please enter your email address first.',
  
  // Passkey messages
  PASSKEY_NOT_SUPPORTED: 'Passkey authentication is not supported on this device.',
  STARTING_PASSKEY_AUTH: 'Starting passkey authentication...',
  PASSKEY_CANCELLED: 'Passkey authentication was cancelled or no passkey found.',
  PASSKEY_VERIFIED: 'Passkey verified! Completing authentication...',
  PASSKEY_LOGIN_SUCCESS: 'Successfully logged in with passkey!',
  PASSKEY_AUTH_CANCELLED: 'Passkey authentication was cancelled.',
  PASSKEY_SETUP_ENTER_EMAIL: 'Please enter your email for passwordless passkey setup.',
  PASSKEY_SETUP_ENTER_CREDENTIALS: 'Please enter both email and password for passkey setup.',
  PASSKEY_SETUP_SUCCESS: 'You can now login with your passkey or email/password!',
  
  // Verification messages
  VERIFICATION_EMAIL_SENT: 'Verification email sent! Please check your inbox.',
  VERIFICATION_FAILED: 'Failed to send verification email. Please try again later.',
  VERIFICATION_SUCCESS: 'Verification email sent successfully!',
  
  // Verification code messages
  VERIFICATION_CODE_SENT: 'Verification code sent! Please check your email and enter the 6-digit code.',
  VERIFICATION_CODE_INVALID: 'Invalid verification code. Please try again.',
  VERIFICATION_CODE_EXPIRED: 'Verification code has expired. Please request a new one.',
  VERIFICATION_CODE_SUCCESS: 'Email verified successfully!'
} as const;

// Verification code configuration
export const VERIFICATION_CONFIG = {
  // Code generation
  CODE_LENGTH: 6,
  CODE_EXPIRY_MINUTES: 15,
  MAX_VERIFICATION_ATTEMPTS: 3,
  
  // Role assignment codes
  EMPLOYEE_CODE_LENGTH: 8,
  EMPLOYEE_CODE_EXPIRY_HOURS: 24,
  
  // Code types
  CODE_TYPES: {
    EMAIL_VERIFICATION: 'email_verification',
    ROLE_ASSIGNMENT: 'role_assignment',
    PASSKEY_SETUP: 'passkey_setup'
  } as const,
  
  // Roles that can be assigned via codes
  ASSIGNABLE_ROLES: {
    ADMIN: 'admin',
    OWNER: 'owner',
    FINANCE_MANAGER: 'financeManager',
    SALES_MANAGER: 'salesManager',
    MANAGER: 'manager',
    LEAD: 'lead',
    STAFF: 'staff'
  } as const
} as const;

// Passkey configuration constants
const toAndroidAppOrigin = (sha256: string): string => {
  const bytes = sha256.split(':').map((hex) => parseInt(hex, 16));
  const base64 = btoa(String.fromCharCode(...bytes));
  return 'android:apk-key-hash:' + base64.replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');
};

export const PASSKEY_CONFIG = {
  // WebAuthn relying party
  RP_NAME: 'RS Maids Booking',
  RP_ID: 'booking.rsmaidsservice.net',
  DEFAULT_ORIGIN: 'https://booking.rsmaidsservice.net',

  // An Android app signs passkey requests with its own origin: "android:apk-key-hash:" + base64url(SHA-256 of the signing
  // certificate). Built from the one list in androidSigning.ts (debug keys and Google Play's app signing key).
  ANDROID_APP_ORIGINS: ANDROID_SIGNING_SHA256.map(toAndroidAppOrigin),
  
  // Challenge settings
  CHALLENGE_TIMEOUT_MS: 5 * 60 * 1000, // 5 minutes
  CHALLENGE_CLEANUP_INTERVAL_MS: 60 * 60 * 1000, // 1 hour
  
  // Authenticator settings
  USER_VERIFICATION: 'required' as const,
  REQUIRE_RESIDENT_KEY: true,
  ATTESTATION_TYPE: 'none' as const,
  
  // Rate limiting
  RATE_LIMIT: {
    LOGIN_MAX_ATTEMPTS: 10,
    LOGIN_WINDOW_MS: 15 * 60 * 1000, // 15 minutes
    REGISTRATION_MAX_ATTEMPTS: 5,
    REGISTRATION_WINDOW_MS: 15 * 60 * 1000, // 15 minutes
  },
  
  // Device info collection
  COLLECT_DEVICE_INFO: true,
  
  // Timeouts
  REQUEST_TIMEOUT_MS: 30 * 1000, // 30 seconds
} as const;
